import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { analyticsService } from "./analyticsService";
import { PAGE_TITLES, SCROLL_EVENTS, AUTH_EVENTS, CONTENT_EVENTS, SUBSCRIPTION_EVENTS, AUDIO_EVENTS } from "./events";

const SCROLL_MILESTONES = [
  SCROLL_EVENTS.DEPTH_25,
  SCROLL_EVENTS.DEPTH_50,
  SCROLL_EVENTS.DEPTH_75,
  SCROLL_EVENTS.DEPTH_100,
];

export const useAnalytics = () => {
  const location = useLocation();

  const reachedMilestones = useRef(new Set());

  useEffect(() => {
    const page = location.pathname + location.search;
    const title =
      PAGE_TITLES[location.pathname] ||
      document.title ||
      "Página sin título";

    analyticsService.sendPageView({ page, title });

    reachedMilestones.current = new Set();
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        window.scrollY || document.documentElement.scrollTop;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;

      if (docHeight <= 0) return;

      const scrollPercent = Math.round((scrollTop / docHeight) * 100);

      SCROLL_MILESTONES.forEach((milestone) => {
        if (
          scrollPercent >= milestone &&
          !reachedMilestones.current.has(milestone)
        ) {
          reachedMilestones.current.add(milestone);
          analyticsService.sendScrollDepth(milestone);
        }
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location]);

  useEffect(() => {
    const handleClick = (e) => {
      const anchor = e.target.closest("a");
      if (!anchor) return;

      const href = anchor.getAttribute("href") || "";
      const isExternal =
        href.startsWith("http") &&
        !href.includes(window.location.hostname);

      if (isExternal) {
        analyticsService.sendExitClick({
          url: href,
          label: anchor.textContent?.trim() || href,
        });
      }
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  const trackSearch = useCallback((term, section) => {
    analyticsService.sendSiteSearch({ term, section });
  }, []);

  const trackAudio = useCallback((action, audioTitle, duration) => {
    analyticsService.sendAudioInteraction({ action, audioTitle, duration });
  }, []);

  const trackEvent = useCallback(({ category, action, label, value }) => {
    analyticsService.sendEvent({ category, action, label, value });
  }, []);

  return {
    trackSearch,
    trackAudio,
    trackEvent,
    AUTH_EVENTS,
    CONTENT_EVENTS,
    SUBSCRIPTION_EVENTS,
    AUDIO_EVENTS,
  };
};
